import React, { lazy, Suspense, useState } from 'react'
import Box from '@mui/material/Box'
import LoadingPage from '../../pages/LoadingPage'
import type { ListResponse, ColumnDataType } from './types'
import CreateNewAcademicUnit from './CreateNewAcademicUnit'
import { type GridSortItem } from '@mui/x-data-grid'
import DeleteAcademicUnit from './DeleteAcademicUnit'
import PageHeader from '../../components/PageHeader'
import PAGE_TITLES from '../../consts/titles'
import { type CustomBreadcrumbT } from '../../utils/BreadcrumbDataPreparation'
import { exportCsv } from '../../utils/exportCsv'
import api from '../../utils/api'
import { notification, closeNotification } from '../../components/Notifications'
import { processItemActions } from './utils/actionProcessing'
import ExportProcessingModal from '../../components/ExportProcessingModal'
import { useTaskStatus } from '../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../components/ExportBackgroundTaskRunningModal'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

interface AcademicUnitProps {
  list: ListResponse
  loading: boolean
  breadcrumb: CustomBreadcrumbT[]
  onSortModelChange: (sortModel: GridSortItem) => void
  currentSortModel?: GridSortItem
}

const AcademicUnitsFeature: React.FC<AcademicUnitProps> = ({ list, loading, breadcrumb, onSortModelChange, currentSortModel }): JSX.Element => {
  const [loadingExport, setLoadingExport] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')

  const {
    taskId,
    setTaskId,
    showBackgroundModal,
    setShowBackgroundModal,
    isCheckingStatus,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_academicUnit')

  const keepDeleteOnlyInLast = (cdgdata: ListResponse): any => ({
    ...cdgdata,
    data: cdgdata?.data?.map((item, index, arr): any => processItemActions(item, index === arr.length - 1)
    )
  })

  const finalizedList = keepDeleteOnlyInLast(list)

  const rows: Array<Record<string, any>> = finalizedList?.data.map((item: any) => ({
    ...item,
    data: {
      ...item?.data,
      children: item?.data?.children.map((child: any) => child.categoryName)
    }
  }))

  const columns: ColumnDataType = list.keys
  const headerName: string = list?.header?.headerName

  const extraColumnProps = {
    width: 260
  }

  const extraModalProps = {
    width: 'sm'
  }

  const handleExport = async (): Promise<void> => {
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    setLoadingExport(true)
    const toastKey = notification('Preparing download...', { variant: 'loading', persist: true })
    try {
      const sort = currentSortModel && { field: currentSortModel.field, direction: currentSortModel.sort }
      const params = {
        sort: currentSortModel ? JSON.stringify(sort) : undefined
      }

      const response = await api.get('/api/divisions/export/', { params })
      if (response?.status === 202) {
        if (response?.data?.taskId) {
          setTaskId(response.data.taskId)
        }
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data

      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: toastKey })
      } else {
        closeNotification(toastKey)
      }
    } catch (error: any) {
      closeNotification(toastKey)
      notification(error?.response?.data?.error ?? 'Internal server error', { variant: 'error' })
    } finally {
      setLoadingExport(false)
    }
  }

  const isExportDisabled = loadingExport || isCheckingStatus

  return (
    <Box display='flex' flexDirection='column' gap={6}>
      <PageHeader
        title={PAGE_TITLES.ACADEMIC_UNITS}
        breadcrumb={breadcrumb}
      />

      <Suspense fallback={<LoadingPage />}>
        {columns !== undefined && rows !== undefined &&
          <AppTable
            loading={loading}
            topbarTitle={headerName}
            exportCsv={() => { void handleExport() }}
            exportCsvLoading={isExportDisabled}
            rows={rows}
            list={finalizedList}
            onSortModelChange={onSortModelChange}
            columns={columns}
            createAction={<CreateNewAcademicUnit />}
            editAction={<CreateNewAcademicUnit />}
            deleteAction={<DeleteAcademicUnit />}
            featureName="Academic Unit"
            createActionTitle='Create Academic Unit'
            extraColumnProps={extraColumnProps}
            extraModalProps={extraModalProps}
            actionModalEntityDisplayField='categoryName'
          />
        }
      </Suspense>
      <ExportProcessingModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false) }}
        message={exportModalMessage}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showBackgroundModal}
        onClose={() => { setShowBackgroundModal(false) }}
        taskId={taskId ?? ''}
      />
    </Box >
  )
}

export default AcademicUnitsFeature
